import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PushPinIcon from '@mui/icons-material/PushPin';
import NoticeDetailModal from './noticeDetailModal';

const CATEGORY_COLOR = { General: '#94A3B8', Incentive: '#F59E0B', News: '#1A8CFF', Update: '#10B981' };

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const getDuration = (n) => {
    const start = fmtDate(n.startDate);
    const end = fmtDate(n.endDate);
    if (start && end) return `${start} — ${end}`;
    if (start) return `From ${start}`;
    if (end) return `Until ${end}`;
    return null;
};

const snippet = (txt, n = 120) => {
    const s = (txt || '').replace(/\s+/g, ' ').trim();
    return s.length > n ? `${s.slice(0, n)}…` : s;
};

// Member noticeboard: the newest notice as a featured preview card, then the next
// four as short title rows — top 5 at a glance, click any to open.
const DashboardNoticeBoard = ({ notices = [], autoOpenNoticeId }) => {
    const navigate = useNavigate();
    const [selectedNotice, setSelectedNotice] = useState(null);

    const [autoOpened, setAutoOpened] = React.useState(false);
    React.useEffect(() => {
        if (autoOpenNoticeId && notices.length > 0 && !autoOpened) {
            const target = notices.find(n => n.noticeId === autoOpenNoticeId);
            if (target) { setSelectedNotice(target); setAutoOpened(true); }
        }
    }, [autoOpenNoticeId, notices, autoOpened]);

    const sorted = [...notices]
        .sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0) || new Date(b.createdOn) - new Date(a.createdOn));
    const openNotice = (n) => n.campaignId ? navigate(`/incentives/${n.campaignId}/detail`) : setSelectedNotice(n);
    const featured = sorted[0];
    const rest = sorted.slice(1, 6); // 1 featured + up to 5 short rows = max 6

    return (
        <div style={{ background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Noticeboard</h3>
                <span onClick={() => navigate('/settings/noticeboard')} style={{ color: '#1A8CFF', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>View All</span>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
                {!featured ? (
                    <div style={{ color: '#94A3B8', textAlign: 'center', padding: 20, fontSize: 12 }}>No notices</div>
                ) : (
                    <>
                        {/* Featured — the newest notice */}
                        {(() => {
                            const color = CATEGORY_COLOR[featured.category] || '#94A3B8';
                            const snip = snippet(featured.content);
                            return (
                                <div
                                    onClick={() => openNotice(featured)}
                                    style={{ border: '1px solid #eef2f7', borderLeft: `4px solid ${color}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', background: '#fff', transition: 'box-shadow .15s', marginBottom: rest.length ? 14 : 0 }}
                                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'}
                                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                        {featured.isPinned && <PushPinIcon sx={{ fontSize: 14, color: '#EF4444' }} />}
                                        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color, background: `${color}1A`, padding: '2px 8px', borderRadius: 10 }}>{featured.category}</span>
                                        <span style={{ fontSize: 10, color: '#94A3B8', marginLeft: 'auto', whiteSpace: 'nowrap' }}>{getDuration(featured) || fmtDate(featured.createdOn)}</span>
                                    </div>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', lineHeight: 1.3, marginBottom: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{featured.title}</div>
                                    {snip && <div style={{ fontSize: 11.5, color: '#64748B', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{snip}</div>}
                                </div>
                            );
                        })()}

                        {/* Next 4 — short title rows */}
                        {rest.map(n => (
                            <div
                                key={n.noticeId}
                                onClick={() => openNotice(n)}
                                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 2px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                                <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: CATEGORY_COLOR[n.category] || '#94A3B8' }} />
                                <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                                <span style={{ flexShrink: 0, fontSize: 10, color: '#94A3B8', whiteSpace: 'nowrap' }}>{fmtDate(n.createdOn)}</span>
                            </div>
                        ))}
                    </>
                )}
            </div>
            <NoticeDetailModal notice={selectedNotice} open={!!selectedNotice} onClose={() => setSelectedNotice(null)} />
        </div>
    );
};

export default DashboardNoticeBoard;
